// src/components/registration/steps/functions/finalizeUserStructure.js
import { getFunctions, httpsCallable } from "firebase/functions";
import { processAddUserApprovalAction } from "../../../approvalGate/approvalAction";
import { v4 as uuidv4 } from "uuid";

/**
 * finalizeUserStructure
 *
 * Writes the userGroups data to Firestore (via your "firestoreOperations" cloud function),
 * then creates invitation docs (also via "firestoreOperations") for each user,
 * and finally sends emails using "sendEmail".
 *
 * @param {Object} params
 * @param {Array}  params.userGroups         - The new user group hierarchy from StepAddUserGroups
 * @param {string} params.company            - e.g. "BetterBest"
 * @param {string} params.collectionBrand    - e.g. "shared"
 * @param {string} params.systemNameConfig   - e.g. "companyStructure" (the sub-path to store the group structure)
 * @param {string} params.userEmail          - The current user’s email (the one issuing invites)
 *
 * @returns {Object} results  => { structureDocId, totalInvites, inviteDetails[] }
 */
export async function finalizeUserStructure({
  userGroups,
  company,
  collectionBrand,
  systemNameConfig, // e.g. "companyStructure"
  userEmail, // The one who is issuing invites
}) {
  // 1) Initialize your CF references
  const functions = getFunctions();
  const firestoreOps = httpsCallable(functions, "firestoreOperations");
  // Although processAddUserApprovalAction calls sendEmailFn internally,
  // you may still have sendEmailFn defined elsewhere if needed.
  // const sendEmailFn = httpsCallable(functions, "sendEmail");

  // 2) Write the userGroups structure doc to Firestore (via CF)
  const structureDocId = "companyStructure"; // naming convention for the structure doc
  const structureData = {
    userGroups,
    createdAt: new Date().toISOString(),
    createdBy: userEmail,
  };

  // Perform a "write" or "update"—whichever logic you prefer:
  await firestoreOps({
    operation: "write",
    company,
    collectionBrand,
    systemName: systemNameConfig,
    uniqueId: structureDocId,
    documentData: structureData,
  });

  // 3) Now create invitation docs + send each email
  let inviteDetails = [];
  let totalInvites = 0;

  for (const group of userGroups) {
    for (const role of group.roles) {
      for (const invitedEmail of role.users) {
        totalInvites++;

        // Derive targetDetails from the loop:
        const targetDetails = {
          userGroup: group.groupName,
          userRole: role.roleName,
        };

        try {
          // Call the function that writes the user document to the correct collection and sends the email.
          await processAddUserApprovalAction({
            newUserEmail: invitedEmail,
            targetDetails,
            company,
            collectionBrand,
            firestoreOps,
          });
          // If the function resolves successfully, record a success message.
          inviteDetails.push({
            email: invitedEmail,
            status: "success",
            message: `Invitation sent to ${invitedEmail}`,
          });
        } catch (error) {
          console.error(`Failed to process invite for ${invitedEmail}:`, error);
          inviteDetails.push({
            email: invitedEmail,
            status: "error",
            message: `Failed to send invite to ${invitedEmail}. Reason: ${error.message}`,
          });
        }
      }
    }
  }

  // 4) Return a summary of what happened
  return {
    structureDocId,
    totalInvites,
    inviteDetails,
  };
}
